import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
const TOKEN_KEY = "apex_token";
const ORIGINAL_TOKEN_KEY = "apex_original_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      // If a stale impersonation was in flight, wipe it before a fresh login.
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    setUser(false);
  };

  // ----- Impersonation -----
  const impersonate = async (userId) => {
    const { data } = await api.post("/admin/impersonate", { user_id: userId });
    // Preserve the admin's original token so we can swap back.
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) localStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken);
    localStorage.setItem(TOKEN_KEY, data.token);
    // Force a hard reload so every open tab / component picks up the new session.
    window.location.href = "/app";
    return data.user;
  };

  const returnToAdmin = () => {
    const originalToken = localStorage.getItem(ORIGINAL_TOKEN_KEY);
    if (!originalToken) return;
    localStorage.setItem(TOKEN_KEY, originalToken);
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    window.location.href = "/app/users";
  };

  const isImpersonating = !!(user && user._impersonated_by);

  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        setError,
        login,
        register,
        logout,
        refresh,
        impersonate,
        returnToAdmin,
        isImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
